import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios, { AxiosRequestConfig } from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import config from '../config';
import { getOrganizationContext } from '../orgContext';

interface MyOrganization {
  organizationId: number;
  organizationName: string;
  organizationSlug: string;
  isDefault: boolean;
  roles: string[];
}

interface OrganizationSummary {
  organizationId: number;
  name: string;
  slug: string;
}

interface OrganizationMember {
  membershipId: number;
  userId: number;
  auth0Sub: string;
  email: string;
  status: string;
  isDefault: boolean;
  roles: string[];
}

interface MemberEnrollment {
  id: number;
  userId: number;
  courseId: number;
  courseName: string;
  enrollmentDate: string;
  completionPercentage: number;
  isCompleted: boolean;
  completionDate?: string | null;
  subscriptionTier: number;
  stripeSubscriptionId?: string | null;
  subscriptionStartDate?: string | null;
  subscriptionEndDate?: string | null;
}

interface MemberAssignment {
  id: number;
  organizationId: number;
  examId?: number | null;
  examName?: string | null;
  type: string;
  title: string;
  description?: string | null;
  status: string;
  dueAt?: string | null;
  timeLimitMinutes: number;
  maxAttempts: number;
  allowLateSubmission: boolean;
  publishedAt?: string | null;
  questionCount: number;
  targetCount: number;
  createdAt: string;
  updatedAt: string;
  memberAttemptCount?: number;
  latestSubmission?: MemberAssignmentSubmission | null;
}

interface MemberAssignmentSubmission {
  id: number;
  userId: number;
  studentName: string;
  studentEmail: string;
  status: string;
  startedAt: string;
  submittedAt?: string | null;
  updatedAt?: string | null;
  attemptNumber: number;
}

interface TeachingAssignmentSummary {
  id: number;
  teacherUserId: number;
  teacherDisplayName: string;
  studentUserId: number;
  studentDisplayName: string;
  courseId: number;
  courseName: string;
  status: string;
  deliveryType: string;
  isPrimary: boolean;
  startDate: string;
  endDate?: string | null;
}

interface MemberProfileDraft {
  firstName: string;
  lastName: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  bio: string;
  interests: string;
  preferredBrands: string;
}

const STATUS_OPTIONS = ['Invited', 'Active', 'Suspended', 'Left'];
const ROLE_OPTIONS = ['Owner', 'Principal', 'Staff', 'Teacher', 'Parent', 'Student'];
const hasOwnerRole = (roles: string[]): boolean => roles.some(role => role.trim().toLowerCase() === 'owner');
const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
const reviewableAttemptStatuses = new Set(['submitted', 'autoevaluated', 'reviewed']);

type TabKey = 'details' | 'roles' | 'enrollments' | 'assignments';
type MemberDirectoryView = 'all' | 'staff' | 'teachers' | 'students';

const normalizedRole = (value: string): string => value.trim().toLowerCase();

const hasRole = (roles: string[], target: string): boolean => roles.some(role => normalizedRole(role) === normalizedRole(target));

const matchesMemberDirectoryView = (member: OrganizationMember, view: MemberDirectoryView): boolean => {
  if (view === 'teachers') {
    return hasRole(member.roles, 'Teacher');
  }

  if (view === 'students') {
    return hasRole(member.roles, 'Student');
  }

  if (view === 'staff') {
    return hasRole(member.roles, 'Owner') || hasRole(member.roles, 'Principal') || hasRole(member.roles, 'Staff');
  }

  return true;
};

const ManageOrganizationMembersAndRoles: React.FC = () => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const apiBaseUrl = `${config.apiBaseUrl}/api/organizations`;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [tab, setTab] = useState<TabKey>('details');

  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number | null>(null);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [roleFilter, setRoleFilter] = useState('All');
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [provisionEmail, setProvisionEmail] = useState('');
  const [provisionFirstName, setProvisionFirstName] = useState('');
  const [provisionLastName, setProvisionLastName] = useState('');
  const [provisionRole, setProvisionRole] = useState('Student');
  const [sendProvisionInvite, setSendProvisionInvite] = useState(true);
  const [roleDrafts, setRoleDrafts] = useState<Record<number, string>>({});
  const [statusDrafts, setStatusDrafts] = useState<Record<number, string>>({});
  const [profileDrafts, setProfileDrafts] = useState<Record<number, MemberProfileDraft>>({});
  const [profileLoadingByMember, setProfileLoadingByMember] = useState<Record<number, boolean>>({});
  const [profileSavingByMember, setProfileSavingByMember] = useState<Record<number, boolean>>({});
  const [profileErrorByMember, setProfileErrorByMember] = useState<Record<number, string>>({});
  const [enrollmentsByMember, setEnrollmentsByMember] = useState<Record<number, MemberEnrollment[]>>({});
  const [enrollmentLoadingByMember, setEnrollmentLoadingByMember] = useState<Record<number, boolean>>({});
  const [enrollmentErrorByMember, setEnrollmentErrorByMember] = useState<Record<number, string>>({});
  const [assignmentsByMember, setAssignmentsByMember] = useState<Record<number, MemberAssignment[]>>({});
  const [assignmentLoadingByMember, setAssignmentLoadingByMember] = useState<Record<number, boolean>>({});
  const [assignmentErrorByMember, setAssignmentErrorByMember] = useState<Record<number, string>>({});
  const [teachingAssignmentsByMember, setTeachingAssignmentsByMember] = useState<Record<number, TeachingAssignmentSummary[]>>({});
  const [teachingAssignmentLoadingByMember, setTeachingAssignmentLoadingByMember] = useState<Record<number, boolean>>({});
  const [teachingAssignmentErrorByMember, setTeachingAssignmentErrorByMember] = useState<Record<number, string>>({});
  const [selectedMembershipId, setSelectedMembershipId] = useState<number | null>(null);

  const activeOrganizationContext = useMemo(() => getOrganizationContext(), []);

  const memberDirectoryView = useMemo<MemberDirectoryView>(() => {
    if (location.pathname === '/manage-staff') {
      return 'staff';
    }

    if (location.pathname === '/manage-teachers') {
      return 'teachers';
    }

    if (location.pathname === '/manage-students') {
      return 'students';
    }

    return 'all';
  }, [location.pathname]);

  const pageConfig = useMemo(() => {
    if (memberDirectoryView === 'staff') {
      return {
        title: 'Staff & Leadership',
        subtitle: 'Manage organization leaders and non-student operational members.',
        createLabel: 'Create Staff User',
        defaultProvisionRole: 'Staff',
        showRoleFilter: true,
        allowedRoleOptions: ['Owner', 'Principal', 'Staff'],
        allRolesLabel: 'All staff roles',
      };
    }

    if (memberDirectoryView === 'teachers') {
      return {
        title: 'Teachers',
        subtitle: 'Manage teaching members and their organization access.',
        createLabel: 'Create Teacher',
        defaultProvisionRole: 'Teacher',
        showRoleFilter: false,
        allowedRoleOptions: ['Teacher'],
        allRolesLabel: 'All teacher roles',
      };
    }

    if (memberDirectoryView === 'students') {
      return {
        title: 'Students',
        subtitle: 'Manage student members, enrollments, and student-facing access.',
        createLabel: 'Create Student',
        defaultProvisionRole: 'Student',
        showRoleFilter: false,
        allowedRoleOptions: ['Student'],
        allRolesLabel: 'All student roles',
      };
    }

    return {
      title: 'Organization Members & Roles',
      subtitle: 'Manage memberships and role assignments in one place.',
      createLabel: 'Create User',
      defaultProvisionRole: 'Student',
      showRoleFilter: true,
      allowedRoleOptions: ROLE_OPTIONS,
      allRolesLabel: 'All roles',
    };
  }, [memberDirectoryView]);

  const requestedOrganizationId = useMemo(() => {
    const value = searchParams.get('organizationId');
    const parsed = value ? Number(value) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [searchParams]);

  const provisionEmailTrimmed = provisionEmail.trim();
  const provisionEmailValid = isValidEmail(provisionEmailTrimmed);

  const clearMessages = () => {
    setError('');
    setNotice('');
  };

  const getManagementToken = useCallback(async (): Promise<string> => {
    const tokenResponse = await getAccessTokenSilently().catch(() =>
      getAccessTokenSilently({ cacheMode: 'off' } as any)
    );

    const token = typeof tokenResponse === 'string'
      ? tokenResponse
      : (tokenResponse as { access_token?: string })?.access_token;

    if (!token) {
      throw new Error('Unable to get access token. Please sign in again and retry.');
    }

    return token;
  }, [getAccessTokenSilently]);

  const withAuth = useCallback(async (request: AxiosRequestConfig) => {
    const token = await getManagementToken();
    return axios({
      ...request,
      headers: {
        ...(request.headers ?? {}),
        Authorization: `Bearer ${token}`,
      },
    });
  }, [getManagementToken]);

  const loadOrganizations = useCallback(async () => {
    try {
      setLoading(true);
      clearMessages();

      const myResponse = await withAuth({ method: 'get', url: `${apiBaseUrl}/my` });
      const platformRolesResponse = await withAuth({ method: 'get', url: `${apiBaseUrl}/platform-user-roles` }).catch(() => null);
      const canUseSuperAdminTools = !!platformRolesResponse;
      const allResponse = canUseSuperAdminTools
        ? await withAuth({ method: 'get', url: `${apiBaseUrl}/discover` }).catch(() => null)
        : null;

      const myOrgs = (myResponse.data ?? []) as MyOrganization[];
      const allOrgs = (allResponse?.data ?? []) as OrganizationSummary[];

      setIsSuperAdmin(canUseSuperAdminTools);

      const ownerOrganizations = myOrgs.filter(org => hasOwnerRole(org.roles));
      const activeOrganizationId = activeOrganizationContext?.organizationId ?? null;
      const defaultMyOrg = ownerOrganizations.find(org => org.organizationId === activeOrganizationId)?.organizationId
        ?? ownerOrganizations.find(org => org.isDefault)?.organizationId
        ?? ownerOrganizations[0]?.organizationId;
      const defaultAllOrg = allOrgs[0]?.organizationId;
      const resolved = canUseSuperAdminTools
        ? (requestedOrganizationId ?? activeOrganizationId ?? defaultAllOrg ?? defaultMyOrg ?? null)
        : (activeOrganizationId ?? requestedOrganizationId ?? defaultMyOrg ?? null);

      setSelectedOrganizationId(prev => prev ?? resolved);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to load organizations.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [activeOrganizationContext?.organizationId, apiBaseUrl, requestedOrganizationId, withAuth]);

  const loadMembers = useCallback(async (organizationId: number) => {
    try {
      setLoading(true);
      clearMessages();
      const response = await withAuth({ method: 'get', url: `${apiBaseUrl}/${organizationId}/members` });
      setMembers((response.data ?? []) as OrganizationMember[]);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to load memberships.';
      setError(message);
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, withAuth]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    loadOrganizations();
  }, [isAuthenticated, loadOrganizations]);

  useEffect(() => {
    if (!requestedOrganizationId || !isSuperAdmin) {
      return;
    }

    setSelectedOrganizationId(requestedOrganizationId);
  }, [isSuperAdmin, requestedOrganizationId]);

  useEffect(() => {
    if (isSuperAdmin) {
      return;
    }

    if (activeOrganizationContext?.organizationId) {
      setSelectedOrganizationId(activeOrganizationContext.organizationId);
    }
  }, [activeOrganizationContext?.organizationId, isSuperAdmin]);

  useEffect(() => {
    if (!isAuthenticated || !selectedOrganizationId) {
      setMembers([]);
      return;
    }

    loadMembers(selectedOrganizationId);
  }, [isAuthenticated, loadMembers, selectedOrganizationId]);

  const handleStatusChange = async (membershipId: number, status: string) => {
    if (!selectedOrganizationId) {
      return;
    }

    try {
      setSaving(true);
      clearMessages();
      await withAuth({
        method: 'patch',
        url: `${apiBaseUrl}/${selectedOrganizationId}/members/${membershipId}/status`,
        data: { status },
      });
      setNotice('Membership status updated.');
      await loadMembers(selectedOrganizationId);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Unable to update membership status.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleAssignRole = async (membershipId: number, roleKey: string) => {
    if (!selectedOrganizationId) {
      return;
    }

    try {
      setSaving(true);
      clearMessages();
      await withAuth({
        method: 'post',
        url: `${apiBaseUrl}/${selectedOrganizationId}/members/${membershipId}/roles`,
        data: { roleKey },
      });
      setNotice(`Assigned ${roleKey}.`);
      await loadMembers(selectedOrganizationId);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Unable to assign role.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveRole = async (membershipId: number, roleKey: string) => {
    if (!selectedOrganizationId) {
      return;
    }

    try {
      setSaving(true);
      clearMessages();
      await withAuth({
        method: 'delete',
        url: `${apiBaseUrl}/${selectedOrganizationId}/members/${membershipId}/roles/${encodeURIComponent(roleKey)}`,
      });
      setNotice(`Removed ${roleKey}.`);
      await loadMembers(selectedOrganizationId);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Unable to remove role.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleProvisionMember = async () => {
    if (!selectedOrganizationId) {
      setError('Select an organization first.');
      return;
    }

    if (!provisionEmailTrimmed) {
      setError('Please enter an email to provision.');
      return;
    }

    if (!provisionEmailValid) {
      setError('Please enter a valid provision email address.');
      return;
    }

    try {
      setSaving(true);
      clearMessages();

      const response = await withAuth({
        method: 'post',
        url: `${apiBaseUrl}/${selectedOrganizationId}/members/provision`,
        data: {
          email: provisionEmailTrimmed,
          firstName: provisionFirstName.trim() || null,
          lastName: provisionLastName.trim() || null,
          roleKey: provisionRole,
          sendInviteEmail: sendProvisionInvite,
        },
      });

      const payload = response.data as { message?: string; membershipId?: number } | undefined;
      const message = payload?.message;
      setNotice(message || 'User provisioned successfully.');
      setProvisionEmail('');
      setProvisionFirstName('');
      setProvisionLastName('');
      setProvisionRole('Student');
      setSendProvisionInvite(true);
      setShowCreateUserModal(false);
      setRoleFilter('All');
      await loadMembers(selectedOrganizationId);
      if (payload?.membershipId) {
        setSelectedMembershipId(payload.membershipId);
      }
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Unable to provision user.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const parseListInput = (value: string): string[] => value.split(/[\n,]+/).map(item => item.trim()).filter(Boolean);

  const loadProfileForMember = useCallback(async (membershipId: number, auth0Sub: string) => {
    if (!auth0Sub) {
      return;
    }

    try {
      setProfileLoadingByMember(prev => ({ ...prev, [membershipId]: true }));
      setProfileErrorByMember(prev => ({ ...prev, [membershipId]: '' }));

      const token = await getManagementToken();
      const response = await fetch(`${config.apiBaseUrl}/api/profile/${encodeURIComponent(auth0Sub)}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Unable to load member profile.');
      }

      const data = await response.json();
      setProfileDrafts(prev => ({
        ...prev,
        [membershipId]: {
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          phone: data.phone || '',
          address: data.address || '',
          city: data.city || '',
          state: data.state || '',
          zipCode: data.zipCode || '',
          country: data.country || '',
          bio: data.bio || '',
          interests: Array.isArray(data.interests) ? data.interests.join('\n') : '',
          preferredBrands: Array.isArray(data.preferredBrands) ? data.preferredBrands.join('\n') : '',
        },
      }));
    } catch (err: any) {
      const message = err?.message || 'Unable to load profile.';
      setProfileErrorByMember(prev => ({ ...prev, [membershipId]: message }));
    } finally {
      setProfileLoadingByMember(prev => ({ ...prev, [membershipId]: false }));
    }
  }, [getManagementToken]);

  const saveProfileForMember = useCallback(async (membershipId: number, auth0Sub: string) => {
    if (!auth0Sub) {
      return;
    }

    const draft = profileDrafts[membershipId];
    if (!draft) {
      return;
    }

    try {
      setProfileSavingByMember(prev => ({ ...prev, [membershipId]: true }));
      setProfileErrorByMember(prev => ({ ...prev, [membershipId]: '' }));

      const token = await getManagementToken();
      const response = await fetch(`${config.apiBaseUrl}/api/profile/${encodeURIComponent(auth0Sub)}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: draft.firstName,
          lastName: draft.lastName,
          phone: draft.phone,
          address: draft.address,
          city: draft.city,
          state: draft.state,
          zipCode: draft.zipCode,
          country: draft.country,
          bio: draft.bio,
          interests: parseListInput(draft.interests),
          preferredBrands: parseListInput(draft.preferredBrands),
        }),
      });

      if (!response.ok) {
        throw new Error('Unable to save profile.');
      }

      setNotice('Profile updated.');
    } catch (err: any) {
      const message = err?.message || 'Unable to save profile.';
      setProfileErrorByMember(prev => ({ ...prev, [membershipId]: message }));
    } finally {
      setProfileSavingByMember(prev => ({ ...prev, [membershipId]: false }));
    }
  }, [getManagementToken, profileDrafts]);

  const loadEnrollmentsForMember = useCallback(async (membershipId: number, auth0Sub: string) => {
    if (!auth0Sub) {
      return;
    }

    try {
      setEnrollmentLoadingByMember(prev => ({ ...prev, [membershipId]: true }));
      setEnrollmentErrorByMember(prev => ({ ...prev, [membershipId]: '' }));

      const token = await getManagementToken();
      const response = await fetch(`${config.apiBaseUrl}/api/courses/user/enrollments?auth0Sub=${encodeURIComponent(auth0Sub)}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Unable to load enrollments.');
      }

      const data = await response.json();
      setEnrollmentsByMember(prev => ({ ...prev, [membershipId]: Array.isArray(data) ? data : [] }));
    } catch (err: any) {
      const message = err?.message || 'Unable to load enrollments.';
      setEnrollmentErrorByMember(prev => ({ ...prev, [membershipId]: message }));
    } finally {
      setEnrollmentLoadingByMember(prev => ({ ...prev, [membershipId]: false }));
    }
  }, [getManagementToken]);

  const loadAssignmentsForMember = useCallback(async (membershipId: number, auth0Sub: string, userId: number) => {
    if (!auth0Sub) {
      return;
    }

    try {
      setAssignmentLoadingByMember(prev => ({ ...prev, [membershipId]: true }));
      setAssignmentErrorByMember(prev => ({ ...prev, [membershipId]: '' }));

      const token = await getManagementToken();
      const response = await fetch(`${config.apiBaseUrl}/api/assignments/my?auth0Sub=${encodeURIComponent(auth0Sub)}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Unable to load assignments.');
      }

      const payload = await response.json();
      const assignmentRows: MemberAssignment[] = Array.isArray(payload) ? payload : [];

      const assignmentsWithSubmission = await Promise.all(assignmentRows.map(async assignment => {
        try {
          const submissionsResponse = await fetch(`${config.apiBaseUrl}/api/assignments/${assignment.id}/submissions`, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (!submissionsResponse.ok) {
            return { ...assignment, memberAttemptCount: 0, latestSubmission: null };
          }

          const allAttempts = (await submissionsResponse.json()) as MemberAssignmentSubmission[];
          const memberAttempts = (Array.isArray(allAttempts) ? allAttempts : []).filter(attempt => attempt.userId === userId);
          const latestSubmission = [...memberAttempts].sort((left, right) => {
            const leftTime = new Date(left.submittedAt || left.updatedAt || left.startedAt).getTime();
            const rightTime = new Date(right.submittedAt || right.updatedAt || right.startedAt).getTime();
            return rightTime - leftTime;
          })[0] ?? null;

          return {
            ...assignment,
            memberAttemptCount: memberAttempts.length,
            latestSubmission,
          };
        } catch {
          return { ...assignment, memberAttemptCount: 0, latestSubmission: null };
        }
      }));

      setAssignmentsByMember(prev => ({ ...prev, [membershipId]: assignmentsWithSubmission }));
    } catch (err: any) {
      const message = err?.message || 'Unable to load assignments.';
      setAssignmentErrorByMember(prev => ({ ...prev, [membershipId]: message }));
    } finally {
      setAssignmentLoadingByMember(prev => ({ ...prev, [membershipId]: false }));
    }
  }, [getManagementToken]);

  const loadTeachingAssignmentsForMember = useCallback(async (membershipId: number, userId: number, mode: 'teacher' | 'student') => {
    try {
      setTeachingAssignmentLoadingByMember(prev => ({ ...prev, [membershipId]: true }));
      setTeachingAssignmentErrorByMember(prev => ({ ...prev, [membershipId]: '' }));

      const token = await getManagementToken();
      const queryKey = mode === 'teacher' ? 'teacherUserId' : 'studentUserId';
      const params = new URLSearchParams({ [queryKey]: String(userId) });
      const headers: HeadersInit = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      if (selectedOrganizationId) {
        headers['X-Organization-Id'] = String(selectedOrganizationId);
      }

      const response = await fetch(`${config.apiBaseUrl}/api/teachingassignments?${params.toString()}`, {
        headers,
      });

      if (!response.ok) {
        throw new Error('Unable to load teaching assignments.');
      }

      const payload = await response.json();
      setTeachingAssignmentsByMember(prev => ({ ...prev, [membershipId]: Array.isArray(payload) ? payload : [] }));
    } catch (err: any) {
      const message = err?.message || 'Unable to load teaching assignments.';
      setTeachingAssignmentErrorByMember(prev => ({ ...prev, [membershipId]: message }));
    } finally {
      setTeachingAssignmentLoadingByMember(prev => ({ ...prev, [membershipId]: false }));
    }
  }, [getManagementToken, selectedOrganizationId]);

  const filteredMembers = useMemo(() => {
    const query = searchValue.trim().toLowerCase();

    return members.filter(member => {
      const matchesView = matchesMemberDirectoryView(member, memberDirectoryView);
      const haystack = [member.email, member.auth0Sub, member.roles.join(' '), member.status].join(' ').toLowerCase();
      const matchesSearch = !query || haystack.includes(query);
      const matchesRole = roleFilter === 'All' || member.roles.some(role => normalizedRole(role) === normalizedRole(roleFilter));
      return matchesView && matchesSearch && matchesRole;
    });
  }, [memberDirectoryView, members, roleFilter, searchValue]);

  useEffect(() => {
    if (!pageConfig.showRoleFilter && roleFilter !== 'All') {
      setRoleFilter('All');
      return;
    }

    if (pageConfig.showRoleFilter && roleFilter !== 'All' && !pageConfig.allowedRoleOptions.includes(roleFilter)) {
      setRoleFilter('All');
    }
  }, [pageConfig, roleFilter]);

  const selectedMember = useMemo(() => {
    if (!filteredMembers.length) {
      return null;
    }

    if (selectedMembershipId) {
      return filteredMembers.find(member => member.membershipId === selectedMembershipId) ?? filteredMembers[0];
    }

    return filteredMembers[0];
  }, [filteredMembers, selectedMembershipId]);

  const selectedMemberTeachingMode = useMemo<'teacher' | 'student' | null>(() => {
    if (!selectedMember) {
      return null;
    }

    if (memberDirectoryView === 'teachers') {
      return 'teacher';
    }

    if (memberDirectoryView === 'students') {
      return 'student';
    }

    if (hasRole(selectedMember.roles, 'Teacher')) {
      return 'teacher';
    }

    if (hasRole(selectedMember.roles, 'Student')) {
      return 'student';
    }

    return null;
  }, [memberDirectoryView, selectedMember]);

  useEffect(() => {
    if (!filteredMembers.length) {
      setSelectedMembershipId(null);
      return;
    }

    if (!selectedMembershipId || !filteredMembers.some(member => member.membershipId === selectedMembershipId)) {
      setSelectedMembershipId(filteredMembers[0].membershipId);
    }
  }, [filteredMembers, selectedMembershipId]);

  useEffect(() => {
    if (!selectedMember) {
      return;
    }

    setStatusDrafts(prev => ({
      ...prev,
      [selectedMember.membershipId]: prev[selectedMember.membershipId] ?? selectedMember.status,
    }));

    if (!selectedMember.auth0Sub) {
      return;
    }

    if (!profileDrafts[selectedMember.membershipId]) {
      void loadProfileForMember(selectedMember.membershipId, selectedMember.auth0Sub);
    }

    if (!enrollmentsByMember[selectedMember.membershipId]) {
      void loadEnrollmentsForMember(selectedMember.membershipId, selectedMember.auth0Sub);
    }
  }, [enrollmentsByMember, loadEnrollmentsForMember, loadProfileForMember, profileDrafts, selectedMember]);

  useEffect(() => {
    if (tab !== 'assignments' || !selectedMember?.auth0Sub) {
      return;
    }

    if (!assignmentsByMember[selectedMember.membershipId]) {
      void loadAssignmentsForMember(selectedMember.membershipId, selectedMember.auth0Sub, selectedMember.userId);
    }
  }, [assignmentsByMember, loadAssignmentsForMember, selectedMember, tab]);

  useEffect(() => {
    if (!selectedMember || !selectedMemberTeachingMode) {
      return;
    }

    if (!teachingAssignmentsByMember[selectedMember.membershipId]) {
      void loadTeachingAssignmentsForMember(selectedMember.membershipId, selectedMember.userId, selectedMemberTeachingMode);
    }
  }, [loadTeachingAssignmentsForMember, selectedMember, selectedMemberTeachingMode, teachingAssignmentsByMember]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.5rem', color: '#0f172a' }}>{pageConfig.title}</h2>
          <p style={{ margin: '4px 0 0', color: '#475569' }}>{pageConfig.subtitle}</p>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => {
              clearMessages();
              setProvisionRole(pageConfig.defaultProvisionRole);
              setShowCreateUserModal(true);
            }}
            style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #2563eb', background: '#2563eb', color: '#fff', cursor: 'pointer', fontWeight: 600 }}
          >
            {pageConfig.createLabel}
          </button>
          {pageConfig.showRoleFilter && (
            <select
              value={roleFilter}
              onChange={(event) => setRoleFilter(event.target.value)}
              style={{ minWidth: 180, padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1' }}
            >
              <option value="All">{pageConfig.allRolesLabel}</option>
              {pageConfig.allowedRoleOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {(error || notice) && (
        <div style={{ borderRadius: 8, padding: '10px 12px', background: error ? '#fef2f2' : '#f0fdf4', color: error ? '#b91c1c' : '#166534' }}>
          {error || notice}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', flex: 1, gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            placeholder="Search by member, role, or status"
            style={{ flex: 1, minWidth: 260, padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1' }}
          />
          {pageConfig.showRoleFilter && (
            <select
              value={roleFilter}
              onChange={(event) => setRoleFilter(event.target.value)}
              style={{ minWidth: 180, padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1' }}
            >
              <option value="All">{pageConfig.allRolesLabel}</option>
              {pageConfig.allowedRoleOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          )}
        </div>
        <div style={{ color: '#64748b', fontSize: '0.95rem' }}>{filteredMembers.length} members</div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 1.15fr) minmax(320px, 0.85fr)', gap: 16, alignItems: 'start' }}>
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, background: '#fff', overflow: 'hidden' }}>
          <div style={{ padding: '12px 14px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
            <div style={{ fontWeight: 700, color: '#0f172a' }}>Members list</div>
            <div style={{ color: '#64748b', fontSize: '0.9rem', marginTop: 4 }}>
              Choose a member to inspect or update their access.
            </div>
          </div>

          {filteredMembers.length === 0 ? (
            <div style={{ padding: 20, color: '#64748b' }}>No members found for the current filter.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {filteredMembers.map(member => {
                const isActive = selectedMember?.membershipId === member.membershipId;
                const profileDraft = profileDrafts[member.membershipId];
                const firstName = profileDraft?.firstName?.trim() || '';
                const lastName = profileDraft?.lastName?.trim() || '';
                const primaryLabel = [firstName, lastName].filter(Boolean).join(' ') || member.email || member.auth0Sub || `Member ${member.membershipId}`;
                return (
                  <button
                    key={member.membershipId}
                    type="button"
                    onClick={() => setSelectedMembershipId(member.membershipId)}
                    style={{
                      width: '100%',
                      border: 'none',
                      borderBottom: '1px solid #f1f5f9',
                      background: isActive ? '#eff6ff' : '#fff',
                      textAlign: 'left',
                      padding: '14px 16px',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                      <div>
                        <div style={{ fontWeight: 700, color: '#0f172a' }}>{primaryLabel}</div>
                        <div style={{ color: '#64748b', fontSize: '0.9rem', marginTop: 2 }}>{member.email || member.auth0Sub}</div>
                      </div>
                      <div style={{ textAlign: 'right', minWidth: 92 }}>
                        <div style={{ color: '#2563eb', fontSize: '0.8rem', fontWeight: 600 }}>{member.status}</div>
                        <div style={{ color: '#64748b', fontSize: '0.8rem', marginTop: 2 }}>{member.roles.length} role{member.roles.length === 1 ? '' : 's'}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, background: '#fff', padding: 16, minHeight: 320 }}>
          {!selectedMember ? (
            <div style={{ color: '#64748b' }}>Select a member to view their details.</div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 8, borderBottom: '1px solid #f1f5f9', paddingBottom: 10, marginBottom: 14 }}>
                {(['details', 'roles', 'enrollments', 'assignments'] as TabKey[]).map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setTab(key)}
                    style={{
                      padding: '7px 12px',
                      borderRadius: 999,
                      border: tab === key ? '1px solid #2563eb' : '1px solid #cbd5e1',
                      background: tab === key ? '#eff6ff' : '#fff',
                      color: tab === key ? '#1d4ed8' : '#334155',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {key === 'details' ? 'Details' : key === 'roles' ? 'Roles' : key === 'enrollments' ? 'Enrollments' : 'Assignments'}
                  </button>
                ))}
              </div>

              {tab === 'details' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div>
                    <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a' }}>{selectedMember.email || selectedMember.auth0Sub || `Member ${selectedMember.membershipId}`}</div>
                    <div style={{ color: '#64748b', marginTop: 4 }}>{selectedMember.auth0Sub}</div>
                  </div>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '4px 8px', borderRadius: 999, fontSize: '0.8rem', fontWeight: 600 }}>{selectedMember.status}</span>
                    {selectedMember.roles.length > 0 ? selectedMember.roles.map(role => (
                      <span key={`${selectedMember.membershipId}-${role}`} style={{ background: '#f8fafc', color: '#334155', padding: '4px 8px', borderRadius: 999, fontSize: '0.8rem' }}>{role}</span>
                    )) : <span style={{ color: '#64748b', fontSize: '0.8rem' }}>No roles assigned</span>}
                  </div>

                  <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontWeight: 600, color: '#334155' }}>
                      Membership status
                      <select
                        value={statusDrafts[selectedMember.membershipId] ?? selectedMember.status}
                        onChange={(event) => setStatusDrafts(prev => ({ ...prev, [selectedMember.membershipId]: event.target.value }))}
                        disabled={saving}
                        style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff' }}
                      >
                        {STATUS_OPTIONS.map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </label>
                    <button
                      type="button"
                      onClick={() => handleStatusChange(selectedMember.membershipId, statusDrafts[selectedMember.membershipId] ?? selectedMember.status)}
                      disabled={saving || (statusDrafts[selectedMember.membershipId] ?? selectedMember.status) === selectedMember.status}
                      style={{ marginTop: 10, padding: '8px 12px', borderRadius: 6, border: '1px solid #2563eb', background: '#2563eb', color: '#fff', cursor: 'pointer' }}
                    >
                      Save changes
                    </button>
                  </div>

                  {selectedMemberTeachingMode && (
                    <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <div style={{ fontWeight: 700, color: '#334155' }}>
                          {selectedMemberTeachingMode === 'teacher' ? 'Student Subject Assignments' : 'Teacher Subject Assignments'}
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <button
                            type="button"
                            onClick={() => void loadTeachingAssignmentsForMember(selectedMember.membershipId, selectedMember.userId, selectedMemberTeachingMode)}
                            style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', cursor: 'pointer' }}
                          >
                            Refresh
                          </button>
                          <Link
                            to={selectedMemberTeachingMode === 'teacher'
                              ? `/manage-teaching-assignments?teacherUserId=${selectedMember.userId}`
                              : `/manage-teaching-assignments?studentUserId=${selectedMember.userId}`}
                            style={{ display: 'inline-block', padding: '6px 10px', borderRadius: 8, border: '1px solid #2563eb', color: '#1d4ed8', textDecoration: 'none', fontSize: '0.85rem', fontWeight: 600 }}
                          >
                            Manage assignments
                          </Link>
                        </div>
                      </div>

                      {teachingAssignmentLoadingByMember[selectedMember.membershipId] ? (
                        <div style={{ color: '#64748b', marginTop: 8 }}>Loading teaching assignments...</div>
                      ) : teachingAssignmentErrorByMember[selectedMember.membershipId] ? (
                        <div style={{ color: '#b91c1c', marginTop: 8 }}>{teachingAssignmentErrorByMember[selectedMember.membershipId]}</div>
                      ) : (teachingAssignmentsByMember[selectedMember.membershipId] ?? []).length === 0 ? (
                        <div style={{ color: '#64748b', marginTop: 8 }}>No teaching assignments found.</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '4px 8px', borderRadius: 999, fontSize: '0.8rem' }}>
                              {(teachingAssignmentsByMember[selectedMember.membershipId] ?? []).length} total
                            </span>
                            <span style={{ background: '#ecfeff', color: '#0e7490', padding: '4px 8px', borderRadius: 999, fontSize: '0.8rem' }}>
                              {(teachingAssignmentsByMember[selectedMember.membershipId] ?? []).filter(item => item.status === 'Active').length} active
                            </span>
                            <span style={{ background: '#fffbeb', color: '#92400e', padding: '4px 8px', borderRadius: 999, fontSize: '0.8rem' }}>
                              {(teachingAssignmentsByMember[selectedMember.membershipId] ?? []).filter(item => item.status === 'Paused').length} paused
                            </span>
                          </div>

                          {(teachingAssignmentsByMember[selectedMember.membershipId] ?? []).slice(0, 5).map(item => (
                            <div key={item.id} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 10, background: '#f8fafc' }}>
                              <div style={{ fontWeight: 700, color: '#0f172a' }}>{item.courseName}</div>
                              <div style={{ color: '#64748b', fontSize: '0.86rem', marginTop: 4 }}>
                                {selectedMemberTeachingMode === 'teacher' ? `Student: ${item.studentDisplayName}` : `Teacher: ${item.teacherDisplayName}`}
                              </div>
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
                                <span style={{ background: '#fff', color: '#334155', padding: '3px 7px', borderRadius: 999, fontSize: '0.78rem' }}>{item.status}</span>
                                <span style={{ background: '#fff', color: '#334155', padding: '3px 7px', borderRadius: 999, fontSize: '0.78rem' }}>{item.deliveryType}</span>
                                {item.isPrimary && <span style={{ background: '#dcfce7', color: '#166534', padding: '3px 7px', borderRadius: 999, fontSize: '0.78rem' }}>Primary</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
                    <div style={{ fontWeight: 700, color: '#334155', marginBottom: 8 }}>Profile & interests</div>
                    {profileLoadingByMember[selectedMember.membershipId] ? (
                      <div style={{ color: '#64748b' }}>Loading profile…</div>
                    ) : profileDrafts[selectedMember.membershipId] ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
                          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontWeight: 600, color: '#334155' }}>
                            First name
                            <input
                              value={profileDrafts[selectedMember.membershipId].firstName}
                              onChange={(event) => setProfileDrafts(prev => ({ ...prev, [selectedMember.membershipId]: { ...prev[selectedMember.membershipId], firstName: event.target.value } }))}
                              style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                            />
                          </label>
                          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontWeight: 600, color: '#334155' }}>
                            Last name
                            <input
                              value={profileDrafts[selectedMember.membershipId].lastName}
                              onChange={(event) => setProfileDrafts(prev => ({ ...prev, [selectedMember.membershipId]: { ...prev[selectedMember.membershipId], lastName: event.target.value } }))}
                              style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                            />
                          </label>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
                          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontWeight: 600, color: '#334155' }}>
                            Phone
                            <input
                              value={profileDrafts[selectedMember.membershipId].phone}
                              onChange={(event) => setProfileDrafts(prev => ({ ...prev, [selectedMember.membershipId]: { ...prev[selectedMember.membershipId], phone: event.target.value } }))}
                              style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                            />
                          </label>
                          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontWeight: 600, color: '#334155' }}>
                            Name
                            <input
                              value={`${profileDrafts[selectedMember.membershipId].firstName} ${profileDrafts[selectedMember.membershipId].lastName}`.trim() || selectedMember.email || selectedMember.auth0Sub}
                              readOnly
                              style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#f8fafc' }}
                            />
                          </label>
                        </div>

                        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontWeight: 600, color: '#334155' }}>
                          Address
                          <input
                            value={profileDrafts[selectedMember.membershipId].address}
                            onChange={(event) => setProfileDrafts(prev => ({ ...prev, [selectedMember.membershipId]: { ...prev[selectedMember.membershipId], address: event.target.value } }))}
                            style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                          />
                        </label>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10 }}>
                          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontWeight: 600, color: '#334155' }}>
                            City
                            <input
                              value={profileDrafts[selectedMember.membershipId].city}
                              onChange={(event) => setProfileDrafts(prev => ({ ...prev, [selectedMember.membershipId]: { ...prev[selectedMember.membershipId], city: event.target.value } }))}
                              style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                            />
                          </label>
                          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontWeight: 600, color: '#334155' }}>
                            State
                            <input
                              value={profileDrafts[selectedMember.membershipId].state}
                              onChange={(event) => setProfileDrafts(prev => ({ ...prev, [selectedMember.membershipId]: { ...prev[selectedMember.membershipId], state: event.target.value } }))}
                              style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                            />
                          </label>
                          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontWeight: 600, color: '#334155' }}>
                            ZIP code
                            <input
                              value={profileDrafts[selectedMember.membershipId].zipCode}
                              onChange={(event) => setProfileDrafts(prev => ({ ...prev, [selectedMember.membershipId]: { ...prev[selectedMember.membershipId], zipCode: event.target.value } }))}
                              style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                            />
                          </label>
                        </div>

                        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontWeight: 600, color: '#334155' }}>
                          Country
                          <input
                            value={profileDrafts[selectedMember.membershipId].country}
                            onChange={(event) => setProfileDrafts(prev => ({ ...prev, [selectedMember.membershipId]: { ...prev[selectedMember.membershipId], country: event.target.value } }))}
                            style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                          />
                        </label>

                        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontWeight: 600, color: '#334155' }}>
                          Bio
                          <textarea
                            value={profileDrafts[selectedMember.membershipId].bio}
                            onChange={(event) => setProfileDrafts(prev => ({ ...prev, [selectedMember.membershipId]: { ...prev[selectedMember.membershipId], bio: event.target.value } }))}
                            rows={4}
                            style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                          />
                        </label>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
                          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontWeight: 600, color: '#334155' }}>
                            Interests
                            <textarea
                              value={profileDrafts[selectedMember.membershipId].interests}
                              onChange={(event) => setProfileDrafts(prev => ({ ...prev, [selectedMember.membershipId]: { ...prev[selectedMember.membershipId], interests: event.target.value } }))}
                              rows={4}
                              style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                            />
                          </label>
                          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontWeight: 600, color: '#334155' }}>
                            Preferred brands
                            <textarea
                              value={profileDrafts[selectedMember.membershipId].preferredBrands}
                              onChange={(event) => setProfileDrafts(prev => ({ ...prev, [selectedMember.membershipId]: { ...prev[selectedMember.membershipId], preferredBrands: event.target.value } }))}
                              rows={4}
                              style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                            />
                          </label>
                        </div>

                        {profileErrorByMember[selectedMember.membershipId] && (
                          <div style={{ color: '#b91c1c', fontSize: '0.9rem' }}>{profileErrorByMember[selectedMember.membershipId]}</div>
                        )}

                        <button
                          type="button"
                          onClick={() => saveProfileForMember(selectedMember.membershipId, selectedMember.auth0Sub)}
                          disabled={profileSavingByMember[selectedMember.membershipId]}
                          style={{ alignSelf: 'flex-start', padding: '8px 12px', borderRadius: 6, border: '1px solid #2563eb', background: '#2563eb', color: '#fff', cursor: 'pointer' }}
                        >
                          {profileSavingByMember[selectedMember.membershipId] ? 'Saving...' : 'Save profile'}
                        </button>
                      </div>
                    ) : (
                      <div style={{ color: '#64748b' }}>No profile data available.</div>
                    )}
                  </div>
                </div>
              ) : tab === 'roles' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div>
                    <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a' }}>{selectedMember.email || selectedMember.auth0Sub || `Member ${selectedMember.membershipId}`}</div>
                    <div style={{ color: '#64748b', marginTop: 4 }}>{selectedMember.auth0Sub}</div>
                  </div>

                  <div>
                    <div style={{ fontWeight: 700, color: '#334155', marginBottom: 8 }}>Current roles</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {selectedMember.roles.length > 0 ? selectedMember.roles.map(role => (
                        <button
                          key={`${selectedMember.membershipId}-${role}`}
                          type="button"
                          onClick={() => handleRemoveRole(selectedMember.membershipId, role)}
                          disabled={saving}
                          style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', cursor: 'pointer' }}
                        >
                          {role} ×
                        </button>
                      )) : <span style={{ color: '#64748b' }}>No roles assigned</span>}
                    </div>
                  </div>

                  <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontWeight: 600, color: '#334155' }}>
                      Assign a role
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <select
                          value={roleDrafts[selectedMember.membershipId] ?? ''}
                          onChange={(event) => setRoleDrafts(prev => ({ ...prev, [selectedMember.membershipId]: event.target.value }))}
                          style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', minWidth: 180 }}
                        >
                          <option value="">Select a role</option>
                          {ROLE_OPTIONS.map(option => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => {
                            const roleKey = roleDrafts[selectedMember.membershipId];
                            if (!roleKey) {
                              return;
                            }
                            handleAssignRole(selectedMember.membershipId, roleKey);
                          }}
                          disabled={saving || !roleDrafts[selectedMember.membershipId]}
                          style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #2563eb', background: '#2563eb', color: '#fff', cursor: 'pointer' }}
                        >
                          Save role
                        </button>
                      </div>
                    </label>
                  </div>
                </div>
              ) : tab === 'enrollments' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div>
                    <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a' }}>{selectedMember.email || selectedMember.auth0Sub || `Member ${selectedMember.membershipId}`}</div>
                    <div style={{ color: '#64748b', marginTop: 4 }}>{selectedMember.auth0Sub}</div>
                  </div>

                  {enrollmentLoadingByMember[selectedMember.membershipId] ? (
                    <div style={{ color: '#64748b' }}>Loading enrollments…</div>
                  ) : enrollmentErrorByMember[selectedMember.membershipId] ? (
                    <div style={{ color: '#b91c1c' }}>{enrollmentErrorByMember[selectedMember.membershipId]}</div>
                  ) : (enrollmentsByMember[selectedMember.membershipId] ?? []).length === 0 ? (
                    <div style={{ color: '#64748b' }}>No enrollments found.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {(enrollmentsByMember[selectedMember.membershipId] ?? []).map(enrollment => (
                        <div key={enrollment.id} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, background: '#f8fafc' }}>
                          <div style={{ fontWeight: 700, color: '#0f172a' }}>{enrollment.courseName}</div>
                          <div style={{ color: '#64748b', fontSize: '0.9rem', marginTop: 4 }}>
                            Enrolled {new Date(enrollment.enrollmentDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                            <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '4px 8px', borderRadius: 999, fontSize: '0.8rem' }}>
                              {Math.round(enrollment.completionPercentage)}% complete
                            </span>
                            <span style={{ background: '#fff', color: '#334155', padding: '4px 8px', borderRadius: 999, fontSize: '0.8rem' }}>
                              {enrollment.isCompleted ? 'Completed' : 'In progress'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div>
                    <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a' }}>{selectedMember.email || selectedMember.auth0Sub || `Member ${selectedMember.membershipId}`}</div>
                    <div style={{ color: '#64748b', marginTop: 4 }}>{selectedMember.auth0Sub}</div>
                  </div>

                  {assignmentLoadingByMember[selectedMember.membershipId] ? (
                    <div style={{ color: '#64748b' }}>Loading assignments...</div>
                  ) : assignmentErrorByMember[selectedMember.membershipId] ? (
                    <div style={{ color: '#b91c1c' }}>{assignmentErrorByMember[selectedMember.membershipId]}</div>
                  ) : (assignmentsByMember[selectedMember.membershipId] ?? []).length === 0 ? (
                    <div style={{ color: '#64748b' }}>No assignments found.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {(assignmentsByMember[selectedMember.membershipId] ?? []).map(assignment => (
                        <div key={assignment.id} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, background: '#f8fafc' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                            <div>
                              <div style={{ fontWeight: 700, color: '#0f172a' }}>{assignment.title}</div>
                              <div style={{ color: '#64748b', fontSize: '0.9rem', marginTop: 4 }}>{assignment.type} • {assignment.status}</div>
                            </div>
                            <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '4px 8px', borderRadius: 999, fontSize: '0.8rem' }}>
                              {assignment.questionCount} questions
                            </span>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10, marginTop: 10 }}>
                            <div style={{ color: '#64748b', fontSize: '0.85rem' }}>
                              Due {assignment.dueAt ? new Date(assignment.dueAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'Not set'}
                            </div>
                            <div style={{ color: '#64748b', fontSize: '0.85rem' }}>
                              Attempts {assignment.memberAttemptCount ?? 0}/{assignment.maxAttempts}
                            </div>
                            <div style={{ color: '#64748b', fontSize: '0.85rem' }}>
                              Latest status {assignment.latestSubmission?.status || 'Not started'}
                            </div>
                            <div style={{ color: '#64748b', fontSize: '0.85rem' }}>
                              Submitted {assignment.latestSubmission?.submittedAt ? new Date(assignment.latestSubmission.submittedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}
                            </div>
                          </div>

                          {assignment.latestSubmission && reviewableAttemptStatuses.has((assignment.latestSubmission.status || '').toLowerCase()) && (
                            <div style={{ marginTop: 10 }}>
                              <Link
                                to={`/admin/submissions/${assignment.latestSubmission.id}`}
                                style={{ display: 'inline-block', padding: '6px 10px', borderRadius: 8, border: '1px solid #2563eb', color: '#1d4ed8', textDecoration: 'none', fontSize: '0.85rem', fontWeight: 600 }}
                              >
                                Review submission
                              </Link>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {loading && <div style={{ color: '#64748b' }}>Loading memberships...</div>}

      {showCreateUserModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            padding: 16,
          }}
          onClick={() => {
            if (!saving) {
              setShowCreateUserModal(false);
            }
          }}
        >
          <div
            style={{
              width: 'min(640px, 100%)',
              borderRadius: 14,
              border: '1px solid #dbe4f0',
              background: '#fff',
              boxShadow: '0 20px 40px rgba(15, 23, 42, 0.24)',
              padding: 18,
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#0f172a' }}>Create User</div>
            <div style={{ marginTop: 4, color: '#64748b', fontSize: '0.9rem' }}>
              This creates User, UserProfile, UserSettings and an invited membership. Auth0 is linked later on first sign-in.
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10, marginTop: 14 }}>
              <input
                type="email"
                value={provisionEmail}
                onChange={(event) => setProvisionEmail(event.target.value)}
                placeholder="User email *"
                maxLength={254}
                aria-invalid={provisionEmailTrimmed.length > 0 && !provisionEmailValid}
                style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1' }}
              />
              <select
                value={provisionRole}
                onChange={(event) => setProvisionRole(event.target.value)}
                style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1' }}
              >
                {ROLE_OPTIONS.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              <input
                type="text"
                value={provisionFirstName}
                onChange={(event) => setProvisionFirstName(event.target.value)}
                placeholder="First name (optional)"
                maxLength={100}
                style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1' }}
              />
              <input
                type="text"
                value={provisionLastName}
                onChange={(event) => setProvisionLastName(event.target.value)}
                placeholder="Last name (optional)"
                maxLength={100}
                style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1' }}
              />
            </div>

            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 12, color: '#334155', fontSize: '0.9rem' }}>
              <input
                type="checkbox"
                checked={sendProvisionInvite}
                onChange={(event) => setSendProvisionInvite(event.target.checked)}
              />
              Send invite email now
            </label>

            {provisionEmailTrimmed.length > 0 && !provisionEmailValid && (
              <div style={{ color: '#b91c1c', fontSize: '0.82rem', marginTop: 8 }}>
                Enter a valid email address (example: learner@school.com).
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button
                type="button"
                onClick={() => setShowCreateUserModal(false)}
                disabled={saving}
                style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => { void handleProvisionMember(); }}
                disabled={saving || !selectedOrganizationId || !provisionEmailTrimmed || !provisionEmailValid}
                style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #2563eb', background: '#2563eb', color: '#fff', cursor: 'pointer', fontWeight: 600 }}
              >
                {saving ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageOrganizationMembersAndRoles;
